<i18n>
{
  "en": {
    "productRecommendation": "Product Recommendation",
    "addProductRecommendation": "Add product recommendation",
    "deleteTitle": "Are you sure you want to delete this product recommendation?",
    "deleteError": "There was an error deleting the recommendation",
    "createError": "There was an error creating the recommendation",
    "editError": "There was an error editing the recommendation",
    "deleteRecommendationDescription": "This operation is irreversible",
    "delete": "Delete",
    "noRecommendations": "No product recommendations available",
    "duplicateError": "A product recommendation with these parameters already exists",
    "requestError": "There was an error loading needing data for product recommendations"
  },
  "es": {
    "productRecommendation": "Recomendaciones de productos",
    "addProductRecommendation": "Añadir recomendación de producto",
    "deleteTitle": "¿Estás seguro de que quieres borrar esta recomendación?",
    "deleteError": "Ha habido un error borrando la recomendación",
    "createError": "Ha habido un error creando la recomendación",
    "editError": "Ha habido un error editando la recomendación",
    "deleteRecommendationDescription": "Esta operación es irreversible",
    "delete": "Borrar",
    "noRecommendations": "No hay recomendaciones de producto disponibles",
    "duplicateError": "Ya existe una recomendación de producto con estos parámetros",
    "requestError": "Ha habido un error cargando los datos de las recomendaciones de producto"
  }
}
</i18n>
<template>
  <v-container class="mb-10">
    <div class="d-grid custom-grid">
      <div class="text-center font-weight-bold ma-1 text-md-h6 text-body-1 indexing-title grid-center-x grid-center-y">
        {{ $t('productRecommendation') }}
      </div>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon class="mr-2 indexing-button grid-end-x grid-center-y" v-on="on" v-bind="attrs" @click="openCreateEditModal">
            <v-icon color="grey" large>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('addProductRecommendation') }}</span>
      </v-tooltip>
    </div>
    <dynameat-skeleton v-if="isLoading" skeleton-type="manualConfigList" :container="true"/>
    <dynameat-confirmation-modal :visible="showSpinner" :is-loading="showSpinner"/>
    <div v-if="showEmptyRecommendationList" class="d-flex justify-center text-center grey--text mt-5">
      <span>{{ $t('noRecommendations') }}</span>
    </div>
    <div v-if="!isLoading">
      <v-list v-for="productRecommendation in productRecommendationList" :key="productRecommendation.guid">
        <product-recommendation-card :recommendation="productRecommendation" @edit="onEditOpenModal(productRecommendation)"
                                     @delete="onOpenDeleteModal(productRecommendation)" class="mb-2"/>
      </v-list>
      <product-recommendation-create-edit-modal :visible="createEditModalVisible"
                                                :productList="mainProductList"
                                                :edit-mode="editMode"
                                                :productRecommendation="recommendationSelected"
                                                @confirm-create="onConfirmCreateModal"
                                                @confirm-edit="onConfirmEditModal"
                                                @cancel="onCancelCreateEditModal"/>
    </div>
    <dynameat-confirmation-modal :visible="deleteModalVisible" :title="$t('deleteTitle')"
                                 :description="$t('deleteRecommendationDescription')" :confirm-text="$t('delete')"
                                 @confirm="onConfirmDeleteModal" @cancel="onCloseDeleteModal"/>
  </v-container>
</template>

<script>

import Vue from 'vue';
import Component from 'vue-class-component';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import { Watch } from 'vue-property-decorator';
import { PRODUCT_RECOMMENDATION_CONFLICT } from './api';
import ProductRecommendationCard from './components/ProductRecommendationCard.vue';
import ProductRecommendationCreateEditModal from './components/ProductRecommendationCreateEditModal.vue';

@Component
@Components({
  ProductRecommendationCreateEditModal, ProductRecommendationCard, DynameatConfirmationModal, DynameatSkeleton,
})
@MapGetters(['productRecommendationList', 'isLoading', 'menuEnabledProductList'])
@MapActions(['masterProductListRequest', 'createProductRecommendationRequest', 'editProductRecommendationRequest',
  'removeProductRecommendationRequest', 'setLoading', 'addNotification', 'productRecommendationListRequest'])
export default class ProductRecommendation extends Vue {
  createEditModalVisible = false;

  deleteModalVisible = false;

  productRecommendationToDelete = null;

  showSpinner = false;

  recommendationSelected = null;

  editMode = false;

  get showEmptyRecommendationList() {
    return this.emptyProductRecommendationList && !this.isLoading;
  }

  get emptyProductRecommendationList() {
    return this.productRecommendationList.length < 1;
  }

  get mainProductList() {
    return this.menuEnabledProductList.filter(this.isMainProduct);
  }

  isMainProduct(product) {
    return !this.menuEnabledProductList.some((p) => Object.values(p.sale_formats).includes(product.guid));
  }

  async created() {
    await this.getProductRecommendationList();
    await this.loadProductListData();
  }

  async getProductRecommendationList() {
    try {
      this.setLoading(1);
      await this.productRecommendationListRequest(this.$route.params.pointOfSaleGuid);
    } catch {
      this.addNotification(createErrorNotification(this.$t('requestError')));
    } finally {
      this.setLoading(-1);
    }
  }

  async loadProductListData() {
    try {
      await this.masterProductListRequest(this.$route.params.pointOfSaleGuid);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('productListError')));
    }
  }

  openCreateEditModal() {
    this.editMode = false;
    this.createEditModalVisible = true;
  }

  onEditOpenModal(productRecommendation) {
    this.editMode = true;
    this.recommendationSelected = productRecommendation;
    this.createEditModalVisible = true;
  }

  onCancelCreateEditModal() {
    this.createEditModalVisible = false;
  }

  async onConfirmCreateModal(productRecommendation) {
    this.createEditModalVisible = false;
    try {
      this.showSpinner = true;
      await this.setLoading(1);
      await this.createProductRecommendationRequest(productRecommendation);
    } catch (err) {
      if (err.response.status === 409 && err.response.data === PRODUCT_RECOMMENDATION_CONFLICT) {
        this.addNotification(createErrorNotification(this.$t('duplicateError')));
      } else {
        this.addNotification(createErrorNotification(this.$t('createError')));
      }
    } finally {
      this.showSpinner = false;
      await this.setLoading(-1);
    }
  }

  async onConfirmEditModal(productRecommendation) {
    this.createEditModalVisible = false;
    try {
      this.showSpinner = true;
      await this.setLoading(1);
      await this.editProductRecommendationRequest(productRecommendation);
    } catch (err) {
      this.addNotification(createErrorNotification(this.$t('editError')));
    } finally {
      this.showSpinner = false;
      await this.setLoading(-1);
    }
  }

  onOpenDeleteModal(productRecommendation) {
    this.productRecommendationToDelete = productRecommendation;
    this.deleteModalVisible = true;
  }

  onCloseDeleteModal() {
    this.productRecommendationToDelete = null;
    this.deleteModalVisible = false;
  }

  async onConfirmDeleteModal() {
    this.deleteModalVisible = false;
    try {
      await this.setLoading(1);
      await this.removeProductRecommendationRequest(this.productRecommendationToDelete.guid);
    } catch (err) {
      this.addNotification(createErrorNotification(this.$t('deleteError')));
    } finally {
      await this.setLoading(-1);
    }
  }

  @Watch('$route')
  async onRouteChange(to, from) {
    if (to.params.pointOfSaleGuid !== from.params.pointOfSaleGuid) {
      await this.getProductRecommendationList();
      await this.loadProductListData();
    }
  }
}
</script>

<style scoped>
.custom-grid {
  grid-template-columns: auto auto auto;
  grid-template-rows: auto;
  grid-template-areas: "nothing title button";
}

.indexing-title {
  grid-area: title;
}

.indexing-button {
  grid-area: button;
}
</style>
